// File: webelieve-frontend/src/features/intake/routes/IntakeDashboard.tsx
// EF-2: Refactored intake dashboard — Pipeline CRM as primary view, EdgeFlow monitor as secondary

import React, { useState, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';
import { edgeflowApi, type EdgeFlowMetrics, type EdgeFlowQueue, type DashboardMetrics } from '../api/edgeflowApi';
import type { LeadSummary, ConversionResult } from '../api/intakeCrmApi';
import MetricsOverview from '../components/MetricsOverview';
import IntakeQueue from '../components/IntakeQueue';
import AIAnalysisPanel from '../components/AIAnalysisPanel';
import IntakePipelineView from '../components/IntakePipelineView';
import LeadDetailView from '../components/LeadDetailView';
import PipelineMetricsBar from '../components/PipelineMetricsBar';
import QuickIntakeModal from '../components/QuickIntakeModal';
import ConvertLeadDialog from '../components/ConvertLeadDialog';
import LeadInboxView from '../components/LeadInboxView';

type DashboardView = 'pipeline' | 'inbox' | 'monitor';

const IntakeDashboard: React.FC = () => {
  // View state
  const [activeView, setActiveView] = useState<DashboardView>('pipeline');
  const [selectedLead, setSelectedLead] = useState<LeadSummary | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Quick Intake modal
  const [quickIntakeOpen, setQuickIntakeOpen] = useState(false);

  // Convert dialog
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState<{
    clientId: string;
    clientName: string;
    practiceArea?: string;
    assignedUserId?: string;
  } | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Pipeline callbacks
  const handleLeadClick = useCallback((lead: LeadSummary) => {
    setSelectedLead(lead);
  }, []);

  const handleConvertRequest = useCallback((lead: LeadSummary) => {
    setConvertTarget({
      clientId: lead.clientId,
      clientName: lead.clientName,
      practiceArea: lead.practiceArea,
      assignedUserId: lead.assignedToUserId,
    });
    setConvertDialogOpen(true);
  }, []);

  const handleConvertFromDetail = useCallback((clientId: string) => {
    if (!selectedLead) return;
    setConvertTarget({
      clientId,
      clientName: selectedLead.clientName,
      practiceArea: selectedLead.practiceArea,
      assignedUserId: selectedLead.assignedToUserId,
    });
    setConvertDialogOpen(true);
  }, [selectedLead]);

  const handleConversionSuccess = useCallback((_result: ConversionResult) => {
    setRefreshKey(k => k + 1);
    setSelectedLead(null); // Go back to pipeline after conversion
  }, []);

  const handleQuickIntakeSuccess = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const handleBackToPipeline = useCallback(() => {
    setSelectedLead(null);
  }, []);

  return (
    <Routes>
      <Route index element={
        <div className="h-full flex flex-col">
          {/* If viewing lead detail, show full-screen detail */}
          {selectedLead ? (
            <LeadDetailView
              clientId={selectedLead.clientId}
              onBack={handleBackToPipeline}
              onConvertRequest={handleConvertFromDetail}
            />
          ) : (
            <>
              {/* Header bar */}
              <div className="flex-shrink-0 px-6 pt-6 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Client Intake
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Manage leads, track pipeline, and convert clients
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Search */}
                    {(activeView === 'pipeline') && (
                      <div className="relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          placeholder="Search leads..."
                          className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent w-48"
                        />
                      </div>
                    )}

                    {/* Quick Intake button */}
                    <button
                      onClick={() => setQuickIntakeOpen(true)}
                      className="px-3 py-1.5 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Quick Intake
                    </button>

                    {/* Refresh */}
                    <button
                      onClick={() => setRefreshKey(k => k + 1)}
                      className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                      title="Refresh"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>

                    {/* View toggle */}
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-0.5">
                      <button
                        onClick={() => setActiveView('pipeline')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                          activeView === 'pipeline'
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        Pipeline
                      </button>
                      <button
                        onClick={() => setActiveView('inbox')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                          activeView === 'inbox'
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        Inbox
                      </button>
                      <button
                        onClick={() => setActiveView('monitor')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                          activeView === 'monitor'
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        EdgeFlow Monitor
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content area */}
              <div className="flex-1 overflow-hidden px-6 pb-6">
                {activeView === 'pipeline' && (
                  <PipelineContent
                    searchQuery={searchQuery}
                    refreshKey={refreshKey}
                    onLeadClick={handleLeadClick}
                    onConvertRequest={handleConvertRequest}
                  />
                )}
                {activeView === 'inbox' && (
                  <LeadInboxView onLeadClick={handleLeadClick} />
                )}
                {activeView === 'monitor' && (
                  <EdgeFlowMonitorContent />
                )}
              </div>
            </>
          )}

          {/* Modals */}
          <QuickIntakeModal
            open={quickIntakeOpen}
            onOpenChange={setQuickIntakeOpen}
            onSuccess={handleQuickIntakeSuccess}
          />

          {convertTarget && (
            <ConvertLeadDialog
              open={convertDialogOpen}
              onOpenChange={setConvertDialogOpen}
              clientId={convertTarget.clientId}
              clientName={convertTarget.clientName}
              practiceArea={convertTarget.practiceArea}
              assignedUserId={convertTarget.assignedUserId}
              onSuccess={handleConversionSuccess}
            />
          )}
        </div>
      } />
    </Routes>
  );
};

// --- Pipeline content ---

function PipelineContent({
  searchQuery,
  refreshKey,
  onLeadClick,
  onConvertRequest,
}: {
  searchQuery: string;
  refreshKey: number;
  onLeadClick: (lead: LeadSummary) => void;
  onConvertRequest: (lead: LeadSummary) => void;
}) {
  return (
    <div className="h-full flex flex-col gap-4">
      <PipelineMetricsBar refreshKey={refreshKey} />
      <div className="flex-1 overflow-auto">
        <IntakePipelineView
          searchQuery={searchQuery}
          refreshKey={refreshKey}
          onLeadClick={onLeadClick}
          onConvertRequest={onConvertRequest}
        />
      </div>
    </div>
  );
}

// --- EdgeFlow Monitor content (preserved from original dashboard) ---

// M7.30: EdgeFlow Monitor refactored to consume supervisory truth layer.
function EdgeFlowMonitorContent() {
  const { intakeLoading, setIntakeLoading, setIntakeError } = useAppStore();
  const [dashboardMetrics, setDashboardMetrics] = React.useState<DashboardMetrics | null>(null);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
  const [apiError, setApiError] = React.useState<string | null>(null);
  // M7.30: Queue drill-down state — shared between MetricsOverview cards and IntakeQueue
  const [drillQueue, setDrillQueue] = React.useState<string | null>(null);

  const loadDashboardData = async () => {
    try {
      setIntakeLoading(true);
      setIntakeError(null);
      setApiError(null);
      const data = await edgeflowApi.getDashboardData();
      setDashboardMetrics(data.dashboardMetrics);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load intake dashboard data';
      setIntakeError(errorMessage);
      setApiError(errorMessage);
    } finally {
      setIntakeLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      loadDashboardData();
    } catch (error) {
      console.error('Error in initial load:', error);
      setApiError('Failed to initialize dashboard');
      setIntakeLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    try {
      interval = setInterval(() => {
        if (!intakeLoading && !apiError) {
          loadDashboardData();
        }
      }, 30000);
    } catch (error) {
      console.error('Error setting up auto-refresh:', error);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [intakeLoading, apiError]);

  const handleRefresh = () => { loadDashboardData(); };

  return (
    <div className="space-y-6">
      {lastUpdated && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </p>
      )}

      {/* Loading State */}
      {intakeLoading && !dashboardMetrics && (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <svg className="w-12 h-12 text-lex-accent animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300">Loading EdgeFlow Monitor...</p>
        </div>
      )}

      {/* Dashboard Content — three columns: Posture | Queues | Intelligence */}
      {dashboardMetrics && !intakeLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <MetricsOverview
              supervisory={dashboardMetrics.supervisory}
              onRefresh={handleRefresh}
              onQueueClick={(queueName) => setDrillQueue(queueName)}
            />
          </div>
          <div className="lg:col-span-1">
            <IntakeQueue
              actionableQueues={dashboardMetrics.supervisory.actionable_queues}
              onRefresh={handleRefresh}
              externalDrillQueue={drillQueue}
              onExternalDrillHandled={() => setDrillQueue(null)}
            />
          </div>
          <div className="lg:col-span-1">
            <AIAnalysisPanel
              dashboardMetrics={dashboardMetrics}
              onRefresh={handleRefresh}
            />
          </div>
        </div>
      )}

      {/* Error State */}
      {apiError && !intakeLoading && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Dashboard Data Unavailable</h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">{apiError}</p>
            </div>
            <button onClick={handleRefresh} className="ml-auto px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors">
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default IntakeDashboard;
